import Link from "next/link";
import type { ReactNode } from "react";

import { FilterSubmit } from "../FilterSubmit";
import { FILTER_INPUT_CLS } from "./FormControls";
import { HairlineGrid } from "./Stats";

/**
 * The shared search/filter toolbar for office list pages: a `HairlineGrid` GET
 * form with a search field, optional inline `filters`, a submit + reset, and a
 * right-hand `stats` slot. Pages compose it rather than rebuilding the toolbar
 * markup ("fix once, fix everywhere"):
 * - `filters`  — extra inline controls (`<FilterSelect>` / `<FilterField>`),
 *   shown after the search box; the cell wraps them on narrow screens.
 * - `hidden`   — hidden `<input>`s that carry other query params on submit.
 * - `stats`    — one or more `<StatCell>` on the right.
 * - `children` — full-width rows below (e.g. a status-chip nav).
 * Override `gridClassName` for a non-default column layout (e.g. several stats)
 * and `searchCellClassName` to make the search cell span/shrink to match.
 */
export function ListToolbar({
  q,
  searchName = "q",
  searchAriaLabel = "search",
  placeholder,
  searchClassName = "w-full sm:w-64",
  searchCellClassName = "",
  submitLabel = "filter",
  resetHref,
  showReset,
  filters,
  hidden,
  stats,
  below,
  gridClassName = "sm:grid-cols-[1fr_auto]",
  children,
}: {
  q?: string | null;
  searchName?: string;
  /** Descriptive accessible name for the search field (e.g. "search leads by
   *  name or email"). Defaults to a plain "search". */
  searchAriaLabel?: string;
  placeholder?: string;
  searchClassName?: string;
  searchCellClassName?: string;
  submitLabel?: ReactNode;
  resetHref?: string;
  /** Defaults to "show when there's a search term". Pass explicitly for pages
   *  whose reset should depend on other filters too. */
  showReset?: boolean;
  filters?: ReactNode;
  hidden?: ReactNode;
  stats?: ReactNode;
  /** Full-width second row under search/filters/stats — the HairlineGrid
   *  draws the divider. For controls that belong with the toolbar but act
   *  elsewhere (e.g. bulk buttons driving the table's selection form). */
  below?: ReactNode;
  gridClassName?: string;
  children?: ReactNode;
}) {
  return (
    <HairlineGrid
      as="form"
      method="get"
      rounded
      className={`mb-6 ${gridClassName}`}
    >
      {/* Wrap only when there are extra filter controls to flow onto a second
          row; without them the search field shrinks (flex-1 min-w-0) so the
          submit/reset stay on one row even on a narrow phone. */}
      <div
        className={`flex items-end gap-3 bg-bg-muted p-4 ${filters ? "flex-wrap " : ""}${searchCellClassName}`.trim()}
      >
        {/* With extra filters the search takes the whole first row on a
            phone (basis-full) instead of being crushed to a sliver beside
            the first select; without them it shrinks (flex-1 min-w-0) so
            submit/reset stay on one row. */}
        <label
          className={
            filters
              ? "flex min-w-0 basis-full flex-col gap-1 sm:basis-auto sm:flex-none"
              : "flex min-w-0 flex-1 flex-col gap-1 sm:flex-none"
          }
        >
          <span className="label hidden sm:block">search</span>
          <input
            type="search"
            name={searchName}
            aria-label={searchAriaLabel}
            defaultValue={q ?? ""}
            placeholder={placeholder}
            className={`${FILTER_INPUT_CLS} ${searchClassName}`.trim()}
          />
        </label>
        {filters}
        <FilterSubmit className="shrink-0">{submitLabel}</FilterSubmit>
        {(showReset ?? Boolean(q)) && resetHref && (
          <Link
            href={resetHref}
            className="text-link h-9 shrink-0 px-2 py-2 font-mono text-xs text-fg-subtle transition-colors hover:text-fg"
          >
            reset
          </Link>
        )}
        {hidden}
      </div>
      {stats}
      {below && (
        <div className="flex flex-wrap items-center gap-3 bg-bg-muted p-4 sm:col-span-2">
          {below}
        </div>
      )}
      {children}
    </HairlineGrid>
  );
}
